import { Injectable } from '@angular/core';
import { cloneDeep, last } from 'lodash-es';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';

import {
  Feature,
  FeatureCommands,
  FeatureNames,
  FeaturePropertyKey,
  ScheduleCommands,
  ScheduleProperties,
} from '@vi-iot/client';

import { GTMService } from '@services';
import { FeatureService, InstallationFeaturesStore } from '@store';

import { ScheduleEntry, ScheduleService } from '../../../../../../features/timemanager/services';
import { HeatingCircuitIgnoredModalService } from '../../../../../../+installations/installation/heating-circuit-ignored-modal/heating-circuit-ignored-modal.service';

export enum HEATING_PROGRAMS {
  ECO = 'eco',
  COMFORT = 'comfort',
  NORMAL = 'normal',
}

@Injectable({
  providedIn: 'root',
})
export class HeatingPanelService {
  ngUnsubscribe: Subject<void> = new Subject<void>();
  heatingSchedule$: Observable<Feature<ScheduleProperties, ScheduleCommands>>;
  isEcoMode$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isComfortMode$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  modes: Observable<Feature<any, unknown>>[] = [];

  private _circuitId: string;

  constructor(
    private featureService: FeatureService,
    private gtmService: GTMService,
    private heatingCircuitIgnoredModalService: HeatingCircuitIgnoredModalService,
    private scheduleService: ScheduleService,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  initFeatures(circuitId: string): void {
    this._circuitId = circuitId;
    this.heatingSchedule$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitHeatingSchedule(this._circuitId)
    );
  }

  initHeatingModes(): void {
    this.heatingSchedule$
      .pipe(
        filter((feature) => !!feature?.properties?.entries?.value),
        tap((feature) => this.scheduleService.createSchedule(feature)),
        map((feature) => this.featureService.getScheduleModes(feature)),
        tap((modes) => {
          // `isEcoAsMode` if true then `eco` is a schedule program like `normal` and `reduce` which can be set by editing schedule
          // `isEcoAsMode` if false then `eco` is a special program which can be activated/deactivated by toggle a switcher
          this.isEcoMode$.next(!!modes.find((mode) => mode.name === HEATING_PROGRAMS.ECO));

          // `isComfortAsMode` if true then `comfort` is a schedule program like `normal` and `reduce` which can be set by editing schedule
          // `isComfortAsMode` if false then `comfort` is a special program which can be activated/deactivated by toggle a switcher
          this.isComfortMode$.next(!!modes.find((mode) => mode.name === HEATING_PROGRAMS.COMFORT));

          this.scheduleService.createModes(modes);
        }),
        map((modes) =>
          modes.map((mode) =>
            this.installationFeaturesStore.selectInstallationDeviceFeature(
              FeatureNames.circuitOperatingProgram(this._circuitId, mode.name)
            )
          )
        ),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((modes) => (this.modes = modes));
  }

  onProgramChange(feature: string, checked: boolean): void {
    this.trackModeToggle(checked, feature);

    this.heatingCircuitIgnoredModalService
      .showModalWhenSrcIsActive(true)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        const program = last(feature.split('.'));
        this.installationFeaturesStore.updateFeature(
          FeatureNames.circuitOperatingProgram(this._circuitId, program),
          !checked,
          FeaturePropertyKey.active,
          null,
          !checked ? FeatureCommands.activate : FeatureCommands.deactivate
        );
      });
  }

  trackModeToggle(event: boolean, feature: string): void {
    const program = last(feature.split('.'));
    switch (program) {
      case HEATING_PROGRAMS.ECO:
        this.gtmService.track(event ? 'onOffEcoMode' : 'onOnEcoMode');
        break;

      case HEATING_PROGRAMS.COMFORT:
        this.gtmService.track(event ? 'onOffComfortMode' : 'onOnComfortMode');
        break;

      default:
        break;
    }
  }

  onProgramTemperatureChange(value: number, feature: string): void {
    this.heatingCircuitIgnoredModalService
      .showModalWhenSrcIsActive(true)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        const program = last(feature.split('.'));
        this.installationFeaturesStore.updateFeature(
          FeatureNames.circuitOperatingProgram(this._circuitId, program),
          value,
          'temperature',
          'targetTemperature',
          'setTemperature'
        );
        this.gtmService.track(
          'onSet',
          `Heating circuit's ${program} program temperature`,
          'switchingTimes'
        );
      });
  }

  onScheduleChange(
    event: { scheduleIds: number[]; periods: ScheduleEntry[] },
    feature: Feature<ScheduleProperties, ScheduleCommands>
  ): void {
    this.heatingCircuitIgnoredModalService
      .showModalWhenSrcIsActive(true)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        const schedule = this.scheduleService.getScheduleEntries(feature);
        const value = this.scheduleService.getScheduleValue(
          event.scheduleIds,
          event.periods,
          schedule
        );
        this.installationFeaturesStore.updateFeature(
          FeatureNames.circuitHeatingSchedule(this._circuitId),
          cloneDeep(value),
          'entries',
          'newSchedule',
          'setSchedule'
        );
      });
  }
}
